<template>
  <v-card class="wrapper-card-step-three border-bottom-mobile">
    <v-card class="pa-0 step-card-desktop">
      <Step
        :step="{
          title: t('landingpages.steps.step-3.title'),
          content: t('landingpages.steps.step-3.content'),
        }"
      />
    </v-card>
    <v-card class="substep-card mt-2">
      <Substeps :substeps="substepsContacts" />
    </v-card>
    <Arrows />
    <v-card class="substep-card my-2 my-lg-7">
      <Substeps :substeps="substepsAdvertising" />
    </v-card>
    <v-card class="substep-card my-2 my-lg-7">
      <Substeps
        :show-blurred-backdrop-upselling="showBlurredBackdropUpselling"
        :substeps="substepsSocialMedia"
      />
    </v-card>
  </v-card>
</template>

<script lang="ts" setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import useFeature from '@/composables/useFeature'
import { Topic } from '@/generated/graphql'
import featureNames from '@/lib/featureNames'
import KindType from '@/modules/contentCreator/creator/enums/KindType'
import GET_ACTIVE_TOPICS from '@/modules/contentCreator/creator/queries/getActiveTopics.gql'
import Arrows from '@/modules/landingpage/Arrows.vue'
import Step from '@/modules/landingpage/steps/Step.vue'
import Substeps from '@/modules/landingpage/substeps/Substeps.vue'
import { TrackingPlugin } from '@/plugins/tracking'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const $tracking = inject<TrackingPlugin>('tracking')

const showBlurredBackdropUpselling = ref(false)

const { getFeature } = useFeature()
const canUseGoogleBusiness = computed(
  () => !!getFeature(featureNames.CONTENT_CREATOR)?.config?.postToGoogleBusiness
)

const { result } = useQuery<{ topics: Topic[] }>(GET_ACTIVE_TOPICS, {
  variables: {
    input: {
      landingpageSlug: route.params.slug as string,
    },
  },
})

const topic = computed(() => {
  if (!result.value?.topics) return null
  return result.value.topics.find(
    (topic) => topic.landingpage?.slug === (route.params.slug as string)
  )
})

const substepsContacts = [
  {
    class: 'pr-3',
    lg: '4',
    icon: 'mdi-account-plus',
    title: t('landingpages.steps.substep-1.title'),
    content: t('landingpages.steps.substep-1.content'),
  },
  {
    class: 'pl-lg-0',
    lg: '5',
    icon: 'mdi-account-supervisor',
    title: t('landingpages.steps.substep-2.title'),
    content: t('landingpages.steps.substep-2.content'),
    button: {
      action: () => openContactList(),
      label: t('landingpages.steps.substep-2.button-label'),
    },
  },
]
const substepsAdvertising = [
  {
    class: 'pr-3',
    lg: '4',
    icon: 'mdi-web',
    title: t('landingpages.steps.substep-3.title'),
    content: t('landingpages.steps.substep-3.content'),
  },
  {
    class: 'pl-lg-0',
    lg: '5',
    icon: 'mdi-web-off',
    title: t('landingpages.steps.substep-4.title'),
    content: t('landingpages.steps.substep-4.content'),
    button: {
      action: () => openAdMaterials(),
      label: t('landingpages.steps.substep-4.button-label'),
    },
  },
]
const substepsSocialMedia = computed(() => [
  {
    class: 'pr-3 d-flex flex-column',
    lg: '3',
    icon: 'mdi-folder-multiple-image',
    title: t('landingpages.steps.substep-5.title'),
    content: t('landingpages.steps.substep-5.content'),
    button: {
      action: () => openContentCreator(KindType.facebookPost),
      label: t('landingpages.steps.substep-5.button-label'),
    },
  },
  {
    class: 'pr-3 pl-lg-0 d-flex flex-column',
    lg: '3',
    icon: 'mdi-application-outline',
    title: t('landingpages.steps.substep-6.title'),
    content: t('landingpages.steps.substep-6.content'),
    button: {
      action: () => openContentCreator(KindType.websiteText),
      label: t('landingpages.steps.substep-6.button-label'),
    },
  },
  ...(canUseGoogleBusiness.value
    ? [
        {
          class: 'pr-3 pl-lg-0 d-flex flex-column',
          lg: '3',
          icon: 'mdi-account-circle',
          title: t('landingpages.steps.substep-7.title'),
          content: t('landingpages.steps.substep-7.content'),
          button: {
            action: () => openContentCreator(KindType.gmbNewsPost),
            label: t('landingpages.steps.substep-7.button-label'),
          },
        },
      ]
    : []),
  {
    class: 'pl-lg-0 d-flex flex-column',
    lg: '3',
    icon: 'mdi-email',
    title: t('landingpages.steps.substep-8.title'),
    content: t('landingpages.steps.substep-8.content'),
    button: {
      action: () => openContentCreator(KindType.newsletter),
      label: t('landingpages.steps.substep-8.button-label'),
    },
  },
])

const openContactList = (): void => {
  $tracking?.event('Landingpages', 'Clicked', 'Contact List')
  router.push({
    name: 'leads-view',
    params: { view: 'list' },
    query: { showBooster: 'true', ['topic-slug']: topic.value?.landingpage?.slug || undefined },
  })
}

const openAdMaterials = (): void => {
  $tracking?.event('Landingpages', 'Clicked', 'Ad Material')
  // This goes to the new page ad-materials
  router.push({
    name: 'ad-materials',
    query: { topicIds: topic.value?.id ? [topic.value.id] : undefined },
  })
}

const openContentCreator = async (kind: string): Promise<void> => {
  if (topic.value) {
    $tracking?.event('Landingpages', 'Clicked', 'Content Creator')
    router.push({
      name: 'content-creator',
      query: { kind: kind as string, topicId: topic.value.id as string },
    })
  } else {
    showBlurredBackdropUpselling.value = true
  }
}
</script>

<style scoped>
.wrapper-card-step-three {
  padding: 15px;
  background-color: white;
}
.step-card-desktop {
  display: block;
}
.substep-card {
  padding: 10px 15px 15px 15px;
}

@media (min-width: 961px) {
  .wrapper-card-step-three {
    border: 1px solid #e4e4e4;
    border-radius: 10px !important;
  }
}

@media (max-width: 960px) {
  .wrapper-card-step-three {
    border-bottom: 1px solid #e4e4e4;
    border-left: 1px solid #e4e4e4;
    border-right: 1px solid #e4e4e4;
    border-top: none;
    border-radius: 0;
    padding-bottom: 0;
  }

  .border-bottom-mobile {
    border-radius: 0 0 8px 8px !important;
  }

  .step-card-desktop {
    display: none;
  }
}
</style>
